// XP and Level Calculation Utilities
import { getLevelConfig } from "./levelConfig"

/**
 * Calculate user level based on total points earned
 * Uses configurable level thresholds from levelConfig
 */
export const calculateLevel = (totalPoints) => {
  const config = getLevelConfig()

  // Find the highest level the user has reached
  let currentLevel = 1

  for (const levelData of config.levels) {
    if (totalPoints >= levelData.pointsRequired) {
      currentLevel = levelData.level
    } else {
      break
    }
  }

  // Handle levels beyond the configured ones
  if (totalPoints >= config.levels[config.levels.length - 1].pointsRequired) {
    const lastConfiguredLevel = config.levels[config.levels.length - 1]
    const pointsAboveBase = totalPoints - lastConfiguredLevel.pointsRequired
    const additionalLevels = Math.floor(
      pointsAboveBase / config.pointsPerLevelAfter
    )
    currentLevel = Math.min(
      lastConfiguredLevel.level + additionalLevels,
      config.maxLevel
    )
  }

  return currentLevel
}

/**
 * Calculate XP needed for the next level
 */
export const getXPForNextLevel = (currentLevel) => {
  const config = getLevelConfig()

  // Find the configured level
  const levelData = config.levels.find((l) => l.level === currentLevel + 1)
  if (levelData) {
    return levelData.pointsRequired
  }

  // For levels beyond configured ones
  if (currentLevel >= config.baseLevel) {
    const lastConfiguredLevel = config.levels[config.levels.length - 1]
    const levelsAboveBase = currentLevel + 1 - lastConfiguredLevel.level
    return (
      lastConfiguredLevel.pointsRequired +
      levelsAboveBase * config.pointsPerLevelAfter
    )
  }

  return 0
}

/**
 * Calculate current XP progress toward next level
 */
export const getXPProgress = (totalPoints) => {
  const currentLevel = calculateLevel(totalPoints)
  const currentLevelThreshold =
    currentLevel === 1 ? 0 : getXPForNextLevel(currentLevel - 1)
  const nextLevelThreshold = getXPForNextLevel(currentLevel)

  const currentXP = totalPoints - currentLevelThreshold
  const maxXP = nextLevelThreshold - currentLevelThreshold

  return {
    currentXP,
    maxXP,
    percentage: Math.min((currentXP / maxXP) * 100, 100),
    level: currentLevel,
    totalPoints,
  }
}

/**
 * Get user level info with nice formatting
 */
export const getUserLevelInfo = (totalPoints) => {
  const progress = getXPProgress(totalPoints)

  return {
    level: progress.level,
    currentXP: progress.currentXP,
    nextLevelXP: progress.maxXP,
    progressPercentage: progress.percentage,
    totalPoints: progress.totalPoints,
    isMaxLevel: progress.level >= 50,
  }
}

/**
 * Check if a user leveled up by comparing old and new points
 */
export const checkForLevelUp = (oldPoints, newPoints) => {
  const oldLevel = calculateLevel(oldPoints)
  const newLevel = calculateLevel(newPoints)

  if (newLevel > oldLevel) {
    return {
      leveledUp: true,
      oldLevel,
      newLevel,
      levelsGained: newLevel - oldLevel,
    }
  }

  return {
    leveledUp: false,
    oldLevel,
    newLevel,
    levelsGained: 0,
  }
}
